import { useEffect, useState } from 'react';
import type { AisVessel } from '@boatnav/schemas';
import { useMapController } from '../map/MapShell.js';
import type { MapController } from '../map/MapController.js';
import { useThemeStore } from '../../app/themeStore.js';
import { useAisSourcesStore } from './aisSourcesStore.js';
import {
  useAisStore,
  startAisPolling,
  stopAisPolling,
  notifyAisViewportChanged,
  reconcileAisPolling,
  AIS_MIN_ZOOM,
  type AisViewport,
} from './aisStore.js';
import { VesselSheet } from './VesselSheet.js';
import { msg } from '../../lib/messages.js';
import './ais.css';

function readViewport(controller: MapController): AisViewport {
  const b = controller.map.getBounds();
  return {
    bbox: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
    zoom: controller.map.getZoom(),
  };
}

/**
 * AIS vessel layer (spec 16), same shape as GotoLayer/MeasureLayer: mounted
 * unconditionally on Navigate, but only does anything once the server
 * reports the layer available (BarentsWatch credentials configured — see
 * `aisSourcesStore`) and the on/off toggle is on. Situational awareness
 * only — no following/tracking of other vessels, no CPA/TCPA/alarms
 * (recorded no-alarms decision, plans/README.md).
 */
export function AisLayer(): JSX.Element | null {
  const controller = useMapController();
  const available = useAisSourcesStore((s) => s.available);
  const on = useAisStore((s) => s.on);
  const vessels = useAisStore((s) => s.vessels);
  const selected = useAisStore((s) => s.selected);
  const selectVessel = useAisStore((s) => s.selectVessel);
  const clearSelected = useAisStore((s) => s.clearSelected);
  const theme = useThemeStore((s) => s.theme); // re-tint markers on day/night
  const [zoom, setZoom] = useState<number | null>(null);
  const [capInfo, setCapInfo] = useState<{ shown: number; total: number; capped: boolean } | null>(null);

  const active = available && on;

  // Poll while active: immediate + every 15 s, paused while the tab is
  // hidden or zoom < 10 (aisStore owns that gating).
  useEffect(() => {
    if (!controller || !active) return undefined;
    setZoom(controller.map.getZoom());
    startAisPolling(() => readViewport(controller));

    const onVisibilityChange = (): void => {
      useAisStore.getState().setHidden(document.hidden);
      reconcileAisPolling();
    };
    onVisibilityChange(); // sync current visibility immediately on (re)start
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopAisPolling();
    };
  }, [controller, active]);

  // Immediate re-fetch (debounced ~1 s) when the viewport leaves the last
  // fetched bbox; also keeps the local zoom mirror in sync for the
  // zoom-in hint below.
  useEffect(() => {
    if (!controller || !active) return undefined;
    return controller.onViewChange(() => {
      setZoom(controller.map.getZoom());
      notifyAisViewportChanged();
    });
  }, [controller, active]);

  // Tap a vessel marker → sheet.
  useEffect(() => {
    if (!controller) return undefined;
    controller.setAisVesselClickHandler((v: AisVessel) => selectVessel(v));
    return () => controller.setAisVesselClickHandler(null);
  }, [controller, selectVessel]);

  // Render (or clear) the marker layer.
  useEffect(() => {
    if (!controller) return;
    if (!active || zoom == null || zoom < AIS_MIN_ZOOM) {
      controller.clearAisVessels();
      setCapInfo(null);
      return;
    }
    setCapInfo(controller.setAisVessels(vessels));
  }, [controller, active, zoom, vessels, theme]);

  if (!active) return null;

  return (
    <>
      {zoom != null && zoom < AIS_MIN_ZOOM && <div className="ais-hint-chip">{msg.ais.zoomInHint}</div>}
      {capInfo?.capped && (
        <div className="ais-hint-chip">
          <span className="numeral">{capInfo.shown}</span> {msg.ais.cappedChip}
        </div>
      )}
      {selected && <VesselSheet vessel={selected} onClose={clearSelected} />}
    </>
  );
}
